import { useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';

import { groupsOf } from '@/api/auth';
import { fetchBranding } from '@/api/branding';
import { queryKeys } from '@/api/queryKeys';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { TenantMark } from '@/theme/TenantMark';
import { type } from '@/theme/typography';

import { visibleNavItems } from './navItems';
import { useSession, useSignOut } from './session';

/** The `lg` breakpoint of `DESIGN.md` §10 — above it the sidebar; below it the drawer. */
const LG = '(min-width: 1024px)';

/**
 * Whether the sidebar is currently §10's drawer rather than the fixed column.
 *
 * Everything visual about the drawer is Tailwind's, but `inert` is an attribute and cannot be
 * scoped to a breakpoint, so the breakpoint has to be read here as well. jsdom implements no
 * `matchMedia`; `src/test/setup.ts` stubs it against `window.innerWidth`.
 */
function useIsDrawer(): boolean {
  const [isDrawer, setIsDrawer] = useState(() => !window.matchMedia(LG).matches);

  useEffect(() => {
    const query = window.matchMedia(LG);
    const update = () => setIsDrawer(!query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return isDrawer;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation(['navigation', 'auth']);
  const session = useSession();
  const signOutNow = useSignOut();
  const location = useLocation();
  const isDrawer = useIsDrawer();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);

  const branding = useQuery({
    queryKey: queryKeys.branding(),
    queryFn: fetchBranding,
    staleTime: Infinity,
  });

  /**
   * The drawer closes when the address changes. It **must** depend on the location: this ran on
   * `[]`, at mount, when the state was already `false` — so the drawer stayed over the screen the
   * user had just navigated to, and the code read as if it did not.
   */
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  /**
   * Dismissing the drawer, **whichever of the two dismissals did it.**
   *
   * ⚠️ `Esc` returned focus to the toggle and the backdrop did not: clicking the backdrop closed the
   * drawer and left focus on `<body>` (found in a live browser), so the next `Tab` restarted at the
   * top of the document instead of at the control the user had just used. Two ways to close one
   * dialog, one of which loses the caret, is the kind of difference that only ever shows up for the
   * people who cannot see where focus went. They share this now so they cannot drift again.
   */
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    toggleRef.current?.focus();
  }, []);

  /**
   * §9: an overlay `Esc` cannot dismiss is a trap, and this one covers the whole screen. Focus goes
   * into the drawer when it opens and back to the toggle when it closes — a drawer that opens
   * behind the keyboard's position is a drawer a keyboard user cannot use.
   */
  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    drawerRef.current?.querySelector('a')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      closeDrawer();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, closeDrawer]);

  if (!session.data) return null;

  const items = visibleNavItems(session.data);
  /**
   * ⚠️ **The brand answers first, and BUG-045 does not flip that** — checked rather than assumed.
   * `/branding/` follows the session now, so for a signed-in reader the two agree and there is
   * nothing to flip; where they can differ, the brand is the one to trust. A tenant-less global
   * superuser has `tenant: null` on their session and the host's brand on `/branding/`, which is
   * what they saw a moment before signing in and what the backend deliberately answers them. And
   * putting the session first would be option C — the name from one source while `TenantBrand`
   * takes the accent and the logo from the other — which the entry rejects as *wrong alone*: a
   * sidebar reading one organisation's name in another's colour. All three come off this one
   * cache entry, so they change together or not at all.
   *
   * The session's name is the **fallback while the brand is in flight**, which is exactly when it
   * is needed: `readerChanged` empties the entry on a change of reader, so between sign-in
   * and the new brand arriving this renders the reader's own tenant and never the previous one's.
   */
  const tenantName = branding.data?.tenant?.name ?? session.data.tenant?.name ?? '';
  const groups = groupsOf(session.data);
  /**
   * ⚠️ Off-screen is not gone. The closed drawer is translated out of view and stays in the DOM, so
   * without this a keyboard user tabs through five invisible links before reaching the page (§9).
   * Above `lg` the sidebar is the navigation and is never inert.
   */
  const navigationHidden = isDrawer && !drawerOpen;
  /**
   * The drawer while it is covering the page — and **only** then: above `lg` this same element is
   * the fixed sidebar, which is a complementary landmark and not a dialog.
   *
   * ⚠️ **It was modal by `inert` alone, and said so to nobody.** `<main>` going inert is what makes
   * the drawer modal for the keyboard, and that half works (verified in a browser); but with no
   * `role` a screen reader is never told a dialog opened, so the user hears a navigation appear from
   * nowhere while the page behind them has quietly stopped answering. The role and its name are the
   * announcement the mechanism was missing — `inert` stays exactly as it is (§9: "Dialogs: labelled,
   * focus-trapped, `Esc` closes", all three of which this now is).
   *
   * ⚠️ **And no `aria-modal`, deliberately — it hid the exits from the very users the role is for.**
   * `inert` is what confines the page, and its boundary is chosen: everything behind the drawer goes
   * inert *except* the toggle, which carries the drawer's only named way out and its `aria-expanded`
   * state. `aria-modal="true"` confines an assistive technology to the **dialog subtree** instead —
   * and the toggle is not in it (it lives in the header bar), while the backdrop is `aria-hidden`. So
   * of the three ways out it left `Esc` as the only one reachable in browse mode, and made the
   * open/closed state unreadable exactly while the drawer was open. The role announces the dialog;
   * `inert` enforces it, with the boundary that keeps the exit on the right side.
   */
  const drawerIsModal = isDrawer && drawerOpen;

  return (
    <div className="flex min-h-screen bg-paper-bg">
      {/*
        §9: the navigation is the same on every screen, and without this a keyboard user tabs
        through all of it on every page load. Visually hidden until it takes focus, which is the
        only time it has anything to say.

        ⚠️ It goes `inert` with the drawer, for the same reason `<main>` does. It sits **before**
        the `<aside>` so that it is first in the tab order, so with the drawer open it was still
        reachable while pointing at `#main-content`, which is inert at that moment — an exit that
        goes nowhere. The ways out of the drawer, and what each of them is actually reachable by:
        the **toggle** — pointer, keyboard and browse mode alike, because it sits outside the drawer
        and outside everything that goes inert, which is why no `aria-modal` walls the drawer off
        from it; **`Esc`** — the keyboard, anywhere on the page; the **backdrop** — the pointer only,
        it is `aria-hidden` decoration.
      */}
      <a
        inert={isDrawer && drawerOpen}
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-paper focus:px-3 focus:py-2 focus:text-sm focus:text-ink-900 focus:ring-2 focus:ring-accent"
      >
        {t('navigation:skipToContent')}
      </a>

      <aside
        id="app-navigation"
        ref={drawerRef}
        inert={navigationHidden}
        role={drawerIsModal ? 'dialog' : undefined}
        aria-label={drawerIsModal ? t('navigation:menu') : undefined}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-60 shrink-0 flex-col border-r border-rule bg-paper px-4 py-6',
          /*
           * ⚠️ **The panel gets a viewport-tall box of its own above `lg`, and this line read
           * `lg:static` until 2026-08-12 (BUG-081).** `position: fixed` here is the **drawer** case
           * only, so above `lg` the `<aside>` was an ordinary flex child of `flex min-h-screen` and
           * stretched to the *container's* height — the document's. §3 asks for a *"fixed sidebar"*
           * and §5 calls this the only navigation, and neither survived a page taller than the
           * window: the whole navigation scrolled off with the content, and the account block below,
           * pinned to the bottom of that stretched column, sat ~1479px down the dashboard.
           *
           * `sticky` rather than `fixed`: it stays in flow, so the column keeps its 240px of the row
           * and `<main>` needs no compensating offset — a `fixed` panel would put the content's left
           * margin in a second place that has to agree with `w-60`. `h-screen` is what sizes the box
           * to one viewport, and `top-0` is the edge it sticks to.
           *
           * ⚠️ **`lg:inset-y-auto` is defensive and order-dependent, and it sizes nothing.** Inset
           * properties do not give a sticky box its height — this one is sized by flow and by
           * `h-screen`; `top` and `bottom` are only the constraints it is held against. At exactly
           * 100vh `top: 0` and `bottom: 0` are satisfiable at the same position, so dropping the
           * drawer's `bottom: 0` changes nothing observable today; it starts mattering the day this
           * box is not exactly one viewport tall and two opposing constraints have to be resolved.
           * It must also stay **before** `lg:top-0`: measured against this repository's own
           * `tailwind-merge`, `twMerge('lg:inset-y-auto lg:top-0')` keeps both, while
           * `twMerge('lg:top-0 lg:inset-y-auto')` drops `lg:top-0` **entirely** — so tidying the
           * class order into alphabetical shape would silently unpin the panel. `app.test.tsx`
           * asserts both survive the merge, because nothing else would say a word.
           */
          'lg:sticky lg:inset-y-auto lg:top-0 lg:h-screen lg:translate-x-0',
          drawerOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          'transition-motion',
        )}
      >
        {/* The tenant's mark, or their name when they have not uploaded one (§2, TASK-102). */}
        <TenantMark fallback={tenantName} className="shrink-0" />

        {/*
          ⚠️ **The items scroll inside the panel, never the panel inside the page.** The box above is
          one viewport tall in both cases (`fixed inset-y-0` as the drawer, `h-screen` as the
          sidebar), so a tenant whose navigation outgrows the window has to overflow *somewhere*:
          without `min-h-0` a flex item refuses to shrink below its content and the overflow would
          push the account block off the bottom of the panel — BUG-081's own defect arriving from the
          other end. `flex-1` is what puts the account block at the foot of the panel; today's five
          items leave that space empty, which is why it is built now rather than discovered later.
        */}
        <nav
          className="mt-6 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto"
          aria-label={t('navigation:landmark')}
        >
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'relative flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-motion',
                  /*
                   * ⚠️ **`ring-inset`, because the `<nav>` above clips anything drawn outside this
                   * box.** Tailwind draws `ring-*` as a **`box-shadow`** and `outline-none` removes
                   * the UA outline, so that shadow *is* the whole focus indicator — there is no
                   * second one to fall back on. `overflow-y-auto` sets one axis only, and per CSS
                   * Overflow 3 a `visible` axis paired with a non-`visible` one computes to `auto`:
                   * the nav is a scroll container on **both** axes and clips its descendants' ink
                   * overflow at its padding box whether or not anything actually overflows. The nav
                   * carries no padding of its own — the panel's `px-4` is on the `<aside>` — so each
                   * link's border box *is* the nav's content box, and an outset ring is cut away
                   * left and right on every item and along the top of the first one: a keyboard
                   * user gets two horizontal strokes, or an underline on `Dashboard`, where §9 asks
                   * for a ring. Drawn inside there is nothing to clip, and the panel's `px-4`
                   * rhythm is untouched — which a gutter (`px-1 -mx-1 py-1 -my-1` on the nav) would
                   * not leave alone. `components/ui/Dialog.tsx` scrolls the same way and keeps its
                   * outset rings only because that panel carries `p-6`; this nav is the one scroll
                   * container in the tree with no gutter at all.
                   */
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
                  // §5: the active item is a tint AND a 2px accent bar on the left edge.
                  isActive
                    ? 'bg-accent/[0.08] text-ink-900 before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:rounded-l before:bg-accent'
                    : 'text-ink-500 hover:bg-paper-sunken hover:text-ink-900',
                )
              }
            >
              <item.icon size={16} strokeWidth={1.5} aria-hidden />
              {t(`navigation:${item.labelKey}`)}
            </NavLink>
          ))}
        </nav>

        {/*
          §5: "current user, their group in an eyebrow, sign out".

          ⚠️ **At the foot of the *panel*, by flow — this was `absolute inset-x-4 bottom-6` until
          2026-08-12 (BUG-081).** Absolute positioning here answers to the `<aside>`'s own box, and
          above `lg` that box was the document's height, so the block a reader reaches for to sign
          out sat below the whole page instead of at the bottom of the sidebar. It is the last flex
          child of a `flex-col` panel now, pushed down by the `flex-1` on the navigation above it and
          `shrink-0` so a long navigation scrolls rather than squeezing it.
        */}
        <div className="mt-6 shrink-0">
          <p className="truncate text-xs text-ink-500">
            {t('navigation:signedInAs', { email: session.data.user.email })}
          </p>
          <p className={`${type.eyebrow} mt-1`}>{groups.join(' · ')}</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            onClick={() => void signOutNow()}
          >
            {t('auth:signOut')}
          </Button>
        </div>
      </aside>

      {/*
        The backdrop the drawer never had: it dims the page it is covering and closes on a click,
        which is the pointer half of the `Esc` above. It is decoration for a screen reader — the two
        ways out that carry a name are the toggle and `Esc` — so it is `aria-hidden`.

        It closes through `closeDrawer`, so it puts focus back on the toggle exactly as `Esc` does.
        Being unnamed makes it invisible to a screen reader; it does not make it a pointer-only
        affordance, and dropping focus to `<body>` on the way out is not something an unnamed
        control gets to do differently.
      */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-900/20 lg:hidden"
          onClick={closeDrawer}
          aria-hidden
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-2 border-b border-rule bg-paper px-4 py-2 lg:hidden">
          <Button
            ref={toggleRef}
            variant="ghost"
            size="sm"
            aria-label={drawerOpen ? t('navigation:closeMenu') : t('navigation:openMenu')}
            aria-expanded={drawerOpen}
            aria-controls="app-navigation"
            onClick={() => setDrawerOpen((open) => !open)}
          >
            <Menu size={16} strokeWidth={1.5} aria-hidden />
          </Button>
          <span className="text-xs text-ink-500">{tenantName}</span>
        </div>

        {/*
          The landmark every screen renders into, and the target of the skip link. It goes `inert`
          while the drawer covers it, which is what makes the drawer modal: `Tab` cannot walk out of
          it and into a page nobody can see, and no focus-trap library is needed to say so. The
          toggle stays outside it on purpose — it is the way back.
        */}
        <main
          id="main-content"
          inert={isDrawer && drawerOpen}
          className="mx-auto w-full max-w-[1400px] px-8 py-6"
        >
          {children}
        </main>
      </div>
    </div>
  );
}
